import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  DASHBOARD,
  DASHBOARD_CONFERENCE_HISTORY,
  DASHBOARD_POST_GAME_SEASON,
  DASHBOARD_STATCREW,
} from '../../constants/routes';
import { DashboardModuleKey, DashboardModuleView } from '../../types/dashboard';

type SidebarItem = {
  label: string;
  to: string;
  icon: 'dashboard' | 'statCrew' | 'conferenceHistory' | 'postGameSeason';
  moduleKey?: DashboardModuleKey;
};

const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    label: 'Dashboard',
    to: DASHBOARD,
    icon: 'dashboard',
  },
  {
    label: 'Conference History',
    to: DASHBOARD_CONFERENCE_HISTORY,
    icon: 'conferenceHistory',
    moduleKey: 'conferenceHistory',
  },
  {
    label: 'Post-season Game',
    to: DASHBOARD_POST_GAME_SEASON,
    icon: 'postGameSeason',
    moduleKey: 'postGameSeason',
  },
];

function SidebarIcon({ type }: { type: SidebarItem['icon'] }) {
  const commonProps = {
    className: 'h-5 w-5',
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  if (type === 'dashboard') {
    return (
      <svg {...commonProps}>
        <path d="M4 4H10V10H4V4Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M14 4H20V10H14V4Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M4 14H10V20H4V14Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M14 14H20V20H14V14Z" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }

  if (type === 'statCrew') {
    return (
      <svg {...commonProps}>
        <path d="M7 4H17L20 7V20H7V4Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M17 4V8H20" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10 12H17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M10 16H15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'conferenceHistory') {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M12 8V12L15 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path
        d="M5 15C5 12.7909 6.79086 11 9 11H15C17.2091 11 19 12.7909 19 15V18H5V15Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path d="M8 11L10 7H14L16 11" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="8.5" cy="16" r="1" fill="currentColor" />
      <circle cx="15.5" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatCrewNavSection({ isEmpty }: { isEmpty: boolean }) {
  const location = useLocation();
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const expanded = pinnedOpen || hoverOpen;

  const statPrefix = '/dashboard/statcrew';
  const isInStatCrew = location.pathname.startsWith(statPrefix);

  const parentInactive = isEmpty ? 'text-[#8e9ab2]' : 'text-[#c6d2ea]';

  const childLinkClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-3 py-2 text-[15px] font-medium no-underline transition ${
      isActive ? 'bg-[#1a2642] text-white' : 'text-[#b8c5df] hover:bg-[#16213b] hover:text-white'
    }`;

  return (
    <div
      className={`group/statcrew rounded-xl ${isInStatCrew ? 'bg-[#1a2642]/45' : ''}`}
      onMouseEnter={() => setHoverOpen(true)}
      onMouseLeave={() => setHoverOpen(false)}
    >
      <div className="flex items-stretch gap-0">
        <NavLink
          to={DASHBOARD_STATCREW}
          end
          className={({ isActive }) =>
            `flex min-w-0 flex-1 items-center gap-3 rounded-xl px-4 py-3 text-[17px] font-medium no-underline transition ${
              isActive ? 'bg-[#1a2642] text-white' : `${parentInactive} hover:bg-[#16213b] hover:text-white`
            }`
          }
        >
          <SidebarIcon type="statCrew" />
          <span className="truncate">StatCrew Module</span>
        </NavLink>
        <button
          type="button"
          aria-expanded={expanded}
          aria-label="Toggle StatCrew submenu"
          title="Expand StatCrew links"
          onClick={(e) => {
            e.preventDefault();
            setPinnedOpen((v) => !v);
          }}
          className={`flex shrink-0 items-center justify-center rounded-xl px-3 py-3 transition-opacity duration-150 focus-visible:opacity-100 ${
            expanded
              ? 'opacity-100'
              : 'opacity-0 group-hover/statcrew:opacity-100 focus-visible:opacity-100'
          } ${
            isEmpty
              ? 'text-[#8e9ab2] hover:bg-[#16213b] hover:text-white'
              : 'text-[#c6d2ea] hover:bg-[#16213b] hover:text-white'
          }`}
        >
          <ChevronDown open={expanded} />
        </button>
      </div>

      {expanded && (
        <div className="border-l border-white/12 pb-2 pl-4 ml-4 mr-2 mt-1 space-y-0.5">
          <NavLink to={`${statPrefix}/assets/xml`} className={childLinkClass}>
            XML files
          </NavLink>
          <NavLink to={`${statPrefix}/assets/roster`} className={childLinkClass}>
            Roster
          </NavLink>
          <NavLink to={`${statPrefix}/assets/schedule`} className={childLinkClass}>
            Schedule
          </NavLink>
        </div>
      )}
    </div>
  );
}

interface DashboardSidebarProps {
  modules: Record<DashboardModuleKey, DashboardModuleView>;
}

function DashboardSidebar({ modules }: DashboardSidebarProps) {
  const statEmpty = !modules.statCrew.available;

  return (
    <aside className="w-full bg-[#10192c] text-white md:min-h-[calc(100vh-132px)] md:w-[220px] md:shrink-0">
      <div className="px-4 py-6">
        <div className="mb-4 flex items-center gap-3 border-b border-white/8 pb-5">
          <div className="h-6 w-6 rounded-md bg-[#4182ff]" />
          <span className="text-[19px] font-semibold">Data Portal</span>
        </div>

        <nav className="space-y-2">
          {SIDEBAR_ITEMS.map((item, index) => (
            <React.Fragment key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === DASHBOARD}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-3 text-[17px] font-medium no-underline transition ${
                    isActive
                      ? 'bg-[#1a2642] text-white'
                      : item.moduleKey && !modules[item.moduleKey].available
                        ? 'text-[#8e9ab2] hover:bg-[#16213b] hover:text-white'
                        : 'text-[#c6d2ea] hover:bg-[#16213b] hover:text-white'
                  }`
                }
              >
                <SidebarIcon type={item.icon} />
                <span>{item.label}</span>
              </NavLink>

              {index === 0 && <StatCrewNavSection isEmpty={statEmpty} />}
            </React.Fragment>
          ))}
        </nav>
      </div>
    </aside>
  );
}

export default DashboardSidebar;
